<script lang="ts" setup>
import CustomButton from '@/components/CustomButton.vue';
import Dialog from '@/components/dialogs/Dialog.vue';
import { useDialogStore } from '@/stores/components/dialog';
import { useOrganizationStore } from '@/stores/organization';
import type { OrganizationEmployee } from '@/types/employee';
import type { Organization } from '@/types/organization';
import type { MoveEmployeeRequest } from '@/types/requests/moveEmployeeRequest';
import { getAllAccessibleOrgsFlat } from '@/utils/organization';
import toast from '@/utils/toast';
import { required } from '@/utils/validation';
import { computed, ref, useTemplateRef } from 'vue';

const dialogStore = useDialogStore();
const organizationStore = useOrganizationStore();

const props = defineProps<{
    oldOrg: Organization;
    oldEmployee?: OrganizationEmployee | null;
}>();

const form = useTemplateRef('form');

const request = ref<MoveEmployeeRequest>({} as MoveEmployeeRequest);
const organizations = computed(() => getAllAccessibleOrgsFlat().filter((o) => o._id !== props.oldOrg._id));

function closeDialog() {
    dialogStore.isMoveEmployeeDialogOpen = false;
    request.value = {} as MoveEmployeeRequest;
}

async function moveEmployee() {
    const formValidation = await form.value?.validate();
    if (!formValidation?.valid) return;

    if (!props.oldEmployee || !props.oldEmployee.roles[0]) {
        toast.error('EmployeeLoadFailed');
        return;
    }

    request.value.oldOrgId = props.oldOrg._id;
    request.value.role = props.oldEmployee.roles[0];
    request.value.employeeId = typeof props.oldEmployee.employee === 'string' ? props.oldEmployee.employee : props.oldEmployee.employee._id;

    await organizationStore.moveEmployeeToOrganization(request.value);
}
</script>

<template>
    <Dialog v-model="dialogStore.isMoveEmployeeDialogOpen" title="Medewerker verplaatsen">
        <v-form ref="form">
            De medewerker wordt verwijderd uit <strong>{{ oldOrg.name }}</strong> en toegevoegd aan:
            <v-autocomplete
                v-model="request.newOrgId"
                required
                :rules="[required]"
                placeholder="Maak een keuze"
                :items="organizations"
                item-title="name"
                item-value="_id"
                class="mt-3"
            />
        </v-form>
        <template #actions>
            <CustomButton text="Verplaatsen" @click="moveEmployee" />
            <CustomButton color="red" text="Annuleren" @click="closeDialog" />
        </template>
    </Dialog>
</template>
